import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ringsFromGeocercaItem } from "./mapa-geocercas-cliente";

const DEFAULT_CENTER = { lat: -17.78, lng: -63.18 };
const MAPS_POLL_MS = 150;
const MAPS_POLL_MAX_MS = 8000;
const FIT_BOUNDS_PADDING = 56;

const ROLE_STYLES = {
    inicio: { badge: "bg-emerald-100 text-emerald-900 border-emerald-200" },
    fin: { badge: "bg-red-100 text-red-900 border-red-200" },
    georuta: { badge: "bg-blue-100 text-blue-900 border-blue-200" },
};

/**
 * @param {object | null} item
 * @returns {string}
 */
function itemLabel(item) {
    if (!item) return "";
    return String(item.label ?? item.nombreGeocerca ?? item.value ?? "").trim();
}

/** @param {object} item */
function geocercaHasCoordinates(item) {
    if (!item) return false;
    const rings = ringsFromGeocercaItem(item);
    return rings.some((path) => path.length >= 3);
}

/**
 * @param {object} item
 * @returns {google.maps.LatLngBounds | null}
 */
function boundsFromGeocercaItem(item) {
    if (!item || !window.google?.maps) return null;
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoint = false;
    const rings = ringsFromGeocercaItem(item);
    rings.forEach((path) => {
        path.forEach((pt) => {
            bounds.extend(pt);
            hasPoint = true;
        });
    });
    return hasPoint && !bounds.isEmpty() ? bounds : null;
}

/**
 * @param {google.maps.Map} map
 * @param {google.maps.LatLngBounds} bounds
 * @param {React.MutableRefObject<number | null>} refitTimeoutRef
 */
function applyFitBounds(map, bounds, refitTimeoutRef) {
    if (!map || bounds.isEmpty()) return;
    const refit = () => {
        if (!bounds.isEmpty()) {
            map.fitBounds(bounds, FIT_BOUNDS_PADDING);
        }
    };
    refit();
    window.google.maps.event.addListenerOnce(map, "idle", refit);
    if (refitTimeoutRef.current != null) {
        window.clearTimeout(refitTimeoutRef.current);
    }
    refitTimeoutRef.current = window.setTimeout(refit, 120);
    window.google.maps.event.trigger(map, "resize");
}

/**
 * Map preview for viaje: geocerca inicio, fin and georuta polygons.
 *
 * @param {object | null} props.geocercaInicioSelected
 * @param {object | null} props.geocercaFinSelected
 * @param {object | null} props.georutaSelected
 */
export default function GraficoViajeComponent({
    geocercaInicioSelected = null,
    geocercaFinSelected = null,
    georutaSelected = null,
    className = "",
    minHeightClass = "h-[min(50vh,520px)] min-h-[280px] sm:min-h-[360px]",
}) {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const polygonsRef = useRef([]);
    const refitTimeoutRef = useRef(null);
    const [mapsError, setMapsError] = useState(false);
    const [focusedRole, setFocusedRole] = useState(null);

    const legendItems = useMemo(() => {
        const entries = [
            { role: "inicio", item: geocercaInicioSelected, label: t("label_geocerca_inicio") },
            { role: "fin", item: geocercaFinSelected, label: t("label_geocerca_fin") },
            { role: "georuta", item: georutaSelected, label: t("label_georuta") },
        ];
        return entries.filter((e) => e.item != null);
    }, [geocercaInicioSelected, geocercaFinSelected, georutaSelected, t]);

    /** Unique geocercas to draw (same value selected twice → one polygon, multiple legend entries). */
    const geocercasToDraw = useMemo(() => {
        const seen = new Set();
        const list = [];
        for (const { role, item } of legendItems) {
            const key = item.value ?? itemLabel(item);
            if (key == null || key === "" || seen.has(key)) continue;
            seen.add(key);
            list.push({ ...item, _role: role });
        }
        return list;
    }, [legendItems]);

    const focusGeocercaOnMap = useCallback((role, item) => {
        const map = mapRef.current;
        if (!map || !window.google?.maps || !item) return;
        const bounds = boundsFromGeocercaItem(item);
        if (!bounds) return;
        setFocusedRole(role);
        applyFitBounds(map, bounds, refitTimeoutRef);
    }, []);

    useEffect(() => {
        let cancelled = false;
        let pollTimer = null;
        const start = Date.now();

        const draw = () => {
            if (cancelled || !containerRef.current) return;
            if (!window.google?.maps) {
                if (Date.now() - start > MAPS_POLL_MAX_MS) {
                    setMapsError(true);
                    return;
                }
                pollTimer = window.setTimeout(draw, MAPS_POLL_MS);
                return;
            }

            setMapsError(false);

            if (!mapRef.current) {
                mapRef.current = new window.google.maps.Map(containerRef.current, {
                    center: DEFAULT_CENTER,
                    zoom: 12,
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                    zoomControl: true,
                });
            }

            const map = mapRef.current;
            setFocusedRole(null);
            polygonsRef.current.forEach((p) => p.setMap(null));
            polygonsRef.current = [];
            const bounds = new window.google.maps.LatLngBounds();
            let hasPoint = false;

            geocercasToDraw.forEach((item) => {
                const color = item.colorGeocerca || "#3388ff";
                const rings = ringsFromGeocercaItem(item);

                rings.forEach((path) => {
                    path.forEach((pt) => {
                        bounds.extend(pt);
                        hasPoint = true;
                    });

                    const polygon = new window.google.maps.Polygon({
                        paths: path,
                        strokeColor: color,
                        strokeOpacity: 0.9,
                        strokeWeight: 2,
                        fillColor: color,
                        fillOpacity: 0.3,
                        map,
                    });
                    polygonsRef.current.push(polygon);
                });
            });

            if (hasPoint && !bounds.isEmpty()) {
                applyFitBounds(map, bounds, refitTimeoutRef);
            } else {
                map.setCenter(DEFAULT_CENTER);
                map.setZoom(12);
            }
        };

        draw();

        return () => {
            cancelled = true;
            if (pollTimer) window.clearTimeout(pollTimer);
        };
    }, [geocercasToDraw]);

    useEffect(() => {
        return () => {
            polygonsRef.current.forEach((p) => p.setMap(null));
            polygonsRef.current = [];
            if (refitTimeoutRef.current != null) {
                window.clearTimeout(refitTimeoutRef.current);
            }
            mapRef.current = null;
        };
    }, []);

    return (
        <div className={`w-full ${className}`}>
            {legendItems.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                    {legendItems.map(({ role, item, label }) => {
                        const color = item.colorGeocerca || "#3388ff";
                        const style = ROLE_STYLES[role] ?? ROLE_STYLES.georuta;
                        const hasCoords = geocercaHasCoordinates(item);
                        const isFocused = focusedRole === role;
                        return (
                            <button
                                key={`${role}-${item.value ?? itemLabel(item)}`}
                                type="button"
                                disabled={!hasCoords}
                                title={
                                    hasCoords
                                        ? t("label_ver_geocerca_mapa") || "Ver en el mapa"
                                        : t("label_geocerca_sin_coordenadas") ||
                                          "Sin coordenadas en el mapa"
                                }
                                onClick={() => focusGeocercaOnMap(role, item)}
                                className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-xs font-medium transition-shadow ${style.badge} ${
                                    hasCoords
                                        ? "cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#84B044]/60"
                                        : "cursor-not-allowed opacity-60"
                                } ${isFocused ? "ring-2 ring-[#0F3457] shadow-md" : ""}`}
                            >
                                <span
                                    className="h-3 w-3 shrink-0 rounded-sm border border-black/10"
                                    style={{ backgroundColor: color }}
                                    aria-hidden
                                />
                                <span className="text-gray-600">{label}:</span>
                                <span className="max-w-[10rem] truncate text-gray-900">
                                    {itemLabel(item) || "—"}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            <div className={`relative w-full ${minHeightClass}`}>
                <div
                    ref={containerRef}
                    className="h-full w-full rounded-lg border border-gray-200 bg-gray-100 shadow-inner"
                />
                {mapsError && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/90 p-4 text-center text-sm text-gray-600">
                        {t("label_error_carga_mapa") || "No se pudo cargar el mapa"}
                    </div>
                )}
                {legendItems.length === 0 && !mapsError && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60 p-4 text-center text-sm text-gray-500">
                        {t("label_seleccione_geocerca_mapa") ||
                            "Seleccione geocerca inicio, fin y/o georuta para ver el mapa"}
                    </div>
                )}
            </div>
        </div>
    );
}
